/**
 * Chutt... V2 - Salle de Crise
 * Design system luxury: GlassCard, ScrollReveal, CollapsibleSection, framer-motion
 * Fond legerement teinte #F8F4EE, accents bordeaux #8B1A1A
 */
import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Button,
  Chip,
  Skeleton,
  Alert,
  TextField,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import BoltIcon from '@mui/icons-material/Bolt';
import PushPinIcon from '@mui/icons-material/PushPin';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ScienceIcon from '@mui/icons-material/Science';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { motion, AnimatePresence } from 'framer-motion';

import { crisisAPI, monitoringAPI } from '../services/api';
import GlassCard from '../components/shared/GlassCard';
import ScrollReveal from '../components/shared/ScrollReveal';
import CollapsibleSection from '../components/shared/CollapsibleSection';
import { colors } from '../styles/colors';

// Crisis palette — Bordeaux accents on ivory
const CRISIS_COLORS = {
  bordeaux: colors.bordeaux,
  or: colors.gold,
  bordeauxLight: 'rgba(139, 26, 26, 0.05)',
  bordeauxMed: 'rgba(139, 26, 26, 0.12)',
  orLight: 'rgba(196, 146, 10, 0.08)',
  pageBg: colors.bordeauxBg,
};

const SEVERITY_COLORS = {
  1: CRISIS_COLORS.or,
  2: CRISIS_COLORS.or,
  3: CRISIS_COLORS.bordeaux,
  4: CRISIS_COLORS.bordeaux,
};

const SEVERITY_LABELS = {
  1: 'Incident mineur',
  2: 'Vigilance accrue',
  3: 'Situation critique',
  4: 'Crise majeure',
};

// ── Extracted sub-component: Crisis History List ──
function CrisisHistoryList({ crisisHistory }) {
  if (crisisHistory.length === 0) {
    return (
      <Typography sx={{
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '0.82rem', color: colors.textLight,
        textAlign: 'center', py: 3,
      }}>
        Aucune crise passee enregistree
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {crisisHistory.slice(0, 10).map((crisis, idx) => (
        <motion.div
          key={crisis.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: idx * 0.05 }}
        >
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            p: 1.5, borderRadius: '12px',
            bgcolor: 'rgba(255,255,255,0.4)',
            border: `1px solid ${colors.borderLight}`,
            transition: 'background-color 0.15s',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
          }}>
            <Typography sx={{
              fontSize: '0.72rem', fontFamily: '"DM Sans", sans-serif',
              color: colors.textMed, minWidth: 70,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {new Date(crisis.detected_at).toLocaleDateString('fr-FR')}
            </Typography>
            <Typography sx={{
              fontSize: '0.72rem', fontFamily: '"DM Sans", sans-serif',
              color: colors.textMed, flex: 1,
            }}>
              {(crisis.trigger_type || 'Spike').replace(/_/g, ' ')}
            </Typography>
            <Chip
              label={SEVERITY_LABELS[crisis.severity] || crisis.severity}
              size="small"
              sx={{
                bgcolor: SEVERITY_COLORS[crisis.severity],
                color: 'white',
                fontSize: '0.62rem',
                fontFamily: '"DM Sans", sans-serif',
                height: 20,
              }}
            />
            <Chip
              label={crisis.status === 'resolved' ? 'Resolue' : crisis.status}
              size="small"
              sx={{
                fontSize: '0.62rem',
                fontFamily: '"DM Sans", sans-serif',
                height: 20,
                bgcolor: crisis.status === 'resolved' ? colors.successBg : CRISIS_COLORS.bordeauxLight,
                color: crisis.status === 'resolved' ? colors.success : colors.textMed,
              }}
            />
          </Box>
        </motion.div>
      ))}
    </Box>
  );
}

function CrisisPage() {
  const { selectedBrand, brands, setHasCrisis, crisisData: contextCrisisData, refreshCrisis } = useOutletContext();

  const [loading, setLoading] = useState(true);
  const loadedOnce = useRef(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [simulating, setSimulating] = useState(null);
  const [simulationResults, setSimulationResults] = useState(null);
  const [error, setError] = useState(null);
  const [crisisData, setCrisisData] = useState(contextCrisisData);
  const [crisisHistory, setCrisisHistory] = useState([]);
  const [actionPlan, setActionPlan] = useState([]);
  const [declareDialogOpen, setDeclareDialogOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolveNotes, setResolveNotes] = useState('');
  const [declareForm, setDeclareForm] = useState({ severity: 2, description: '' });
  const [timeline, setTimeline] = useState([]);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const currentBrand = brands?.find(b => b.id === selectedBrand);
  const brandName = currentBrand?.name || '';

  const fetchTimeline = async (crisisId) => {
    try {
      const resp = await crisisAPI.getTimeline(crisisId);
      setTimeline(resp?.data?.timeline || []);
    } catch (e) { console.warn('timeline fetch error', e); }
  };

  const fetchReport = async (crisisId) => {
    setReportLoading(true);
    try {
      const resp = await crisisAPI.getReport(crisisId);
      setReport(resp?.data || null);
    } catch (e) { console.warn('report fetch error', e); }
    setReportLoading(false);
  };

  useEffect(() => {
    if (contextCrisisData) {
      setCrisisData(contextCrisisData);

      if (contextCrisisData?.crisis?.id) {
        fetchTimeline(contextCrisisData.crisis.id);
      }

      if (contextCrisisData?.crisis?.recommended_actions) {
        const actionsTaken = contextCrisisData.crisis.actions_taken || [];
        setActionPlan(contextCrisisData.crisis.recommended_actions.map((action, idx) => {
          const actionText = typeof action === 'string' ? action : action.action;
          const isCompleted = actionsTaken.some(taken =>
            (typeof taken === 'string' ? taken : taken.action) === actionText
          );
          return {
            id: idx,
            text: actionText,
            priority: typeof action === 'object' ? action.priority : 'medium',
            category: typeof action === 'object' ? action.category : null,
            timeline: typeof action === 'object' ? action.timeline : null,
            completed: isCompleted,
          };
        }));
      }
    }
  }, [contextCrisisData]);

  useEffect(() => {
    const fetchHistory = async () => {
      if (!selectedBrand) return;
      if (!loadedOnce.current) setLoading(true);
      setError(null);
      try {
        const historyRes = await crisisAPI.getHistory(selectedBrand);
        setCrisisHistory(historyRes.data?.crises || []);
      } catch (err) {
        console.error('Crisis history fetch error:', err);
        setError('Erreur lors du chargement de l\'historique');
      } finally {
        setLoading(false);
        loadedOnce.current = true;
      }
    };
    fetchHistory();
  }, [selectedBrand]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAnalyze = async () => {
    if (!crisisData?.crisis?.id) return;
    setAnalyzing(true);
    try {
      const res = await crisisAPI.analyze(crisisData.crisis.id);
      setCrisisData(prev => ({
        ...prev,
        crisis: {
          ...prev.crisis,
          ai_analysis: res.data.analysis,
          recommended_actions: res.data.recommended_actions,
        },
      }));
      if (res.data.recommended_actions) {
        setActionPlan(res.data.recommended_actions.map((action, idx) => ({
          id: idx,
          text: typeof action === 'string' ? action : action.action,
          priority: typeof action === 'object' ? action.priority : 'medium',
          category: typeof action === 'object' ? action.category : null,
          timeline: typeof action === 'object' ? action.timeline : null,
          completed: false,
        })));
      }
    } catch (err) {
      console.error('Analysis error:', err);
      setError('Erreur lors de l\'analyse IA');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleToggleAction = async (actionId) => {
    const action = actionPlan.find(a => a.id === actionId);
    if (!action || !crisisData?.crisis?.id) return;
    try {
      if (!action.completed) {
        await crisisAPI.addAction(crisisData.crisis.id, action.text);
      }
      setActionPlan(prev =>
        prev.map(a => a.id === actionId ? { ...a, completed: !a.completed } : a)
      );
    } catch (err) {
      console.error('Action error:', err);
    }
  };

  const handleResolveCrisis = async () => {
    if (!crisisData?.crisis?.id) return;
    try {
      await crisisAPI.resolve(crisisData.crisis.id, resolveNotes, 'success');
      setResolveDialogOpen(false);
      setHasCrisis(false);
      await refreshCrisis();
      const historyRes = await crisisAPI.getHistory(selectedBrand);
      setCrisisHistory(historyRes.data?.crises || []);
    } catch (err) {
      console.error('Resolve error:', err);
      setError('Erreur lors de la resolution');
    }
  };

  const handleSimulation = async (scenario) => {
    if (!currentBrand) return;
    setSimulating(scenario);
    setSimulationResults(null);
    try {
      let keywords = [];
      const name = currentBrand.name;
      switch (scenario) {
        case 'influencer':
          keywords = [`${name} influenceur critique`, `${name} viral negatif`, `${name} bad buzz celebrite`];
          break;
        case 'media':
          keywords = [`${name} scandale media`, `${name} enquete journaliste`, `${name} article critique`];
          break;
        case 'duration':
          keywords = [`${name} crise prolongee`, `${name} reputation impact`, `${name} gestion crise echec`];
          break;
        default:
          keywords = [`${name} crise`];
      }
      const res = await monitoringAPI.search(keywords, null, false);
      const results = res.data?.results || [];
      const negative = results.filter(r => r.sentiment === 'negative').length;
      const total = results.length;
      setSimulationResults({
        scenario, keywords, total, negative,
        sample: results.slice(0, 3),
        analysis: generateSimulationAnalysis(scenario, total, negative, name),
      });
    } catch (err) {
      console.error('Simulation error:', err);
      setError('Erreur lors de la simulation');
    } finally {
      setSimulating(null);
    }
  };

  const generateSimulationAnalysis = (scenario, total, negative, name) => {
    const ratio = total > 0 ? (negative / total * 100).toFixed(0) : 0;
    switch (scenario) {
      case 'influencer':
        return total > 0
          ? `Simulation: Si un influenceur majeur critique ${name}, le risque de propagation est eleve. ${negative} contenus negatifs potentiels detectes sur ${total} resultats (${ratio}% negatif). Recommandation: Preparer un kit de reponse et identifier les ambassadeurs de marque.`
          : `Peu de contenu trouve sur ce scenario. La marque est relativement protegee, mais restez vigilant.`;
      case 'media':
        return total > 0
          ? `Simulation: Une reprise media amplifierait la crise. ${negative} articles potentiellement critiques trouves. Impact estime: portee x10. Recommandation: Preparer un communique de presse et contacter proactivement les journalistes allies.`
          : `Aucun contenu media recent critique trouve. Bonne nouvelle, mais preparez tout de meme vos elements de langage.`;
      case 'duration':
        return total > 0
          ? `Simulation: Une crise prolongee (48h+) entrainerait une accumulation de ${negative} contenus negatifs. Impact sur le NSS: -15 a -30 points estimes. Recommandation: Agir vite pour contenir la crise dans les premieres 24h.`
          : `Impact limite detecte pour une crise prolongee, mais la vigilance reste de mise.`;
      default:
        return 'Analyse non disponible';
    }
  };

  const handleDeclareCrisis = async () => {
    if (!selectedBrand || !declareForm.description.trim()) {
      setError('Veuillez remplir tous les champs');
      return;
    }
    try {
      await crisisAPI.createManual(selectedBrand, declareForm.severity, declareForm.description);
      setDeclareDialogOpen(false);
      setDeclareForm({ severity: 2, description: '' });
      await refreshCrisis();
      setHasCrisis(true);
    } catch (err) {
      console.error('Declare crisis error:', err);
      setError('Erreur lors de la declaration de crise');
    }
  };

  const activeCrisis = crisisData?.has_crisis && crisisData?.crisis;

  if (!selectedBrand) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="textSecondary">
          Veuillez selectionner une marque
        </Typography>
      </Box>
    );
  }

  // AI analysis rendering helper
  const renderAIAnalysis = () => {
    const analysis = crisisData?.crisis?.ai_analysis;
    if (!analysis) {
      return (
        <Typography sx={{
          fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem',
          color: colors.textLight, textAlign: 'center', py: 2,
        }}>
          Cliquez sur "Relancer" pour obtenir une analyse IA de la situation.
        </Typography>
      );
    }

    if (typeof analysis === 'string') {
      return (
        <Typography sx={{
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '0.82rem', color: colors.text,
          whiteSpace: 'pre-line', textAlign: 'justify', lineHeight: 1.7,
        }}>
          {analysis}
        </Typography>
      );
    }

    // Object format with structured fields
    return (
      <Box>
        {analysis.summary && (
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{
              fontFamily: '"DM Sans", sans-serif', fontSize: '0.62rem', fontWeight: 600,
              textTransform: 'uppercase', letterSpacing: '0.1em', color: colors.textMed, mb: 0.5,
              display: 'flex', alignItems: 'center', gap: 0.5,
            }}>
              <AssignmentIcon sx={{ fontSize: '0.85rem' }} /> Resume
            </Typography>
            <Typography sx={{
              fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: colors.text,
              textAlign: 'justify', lineHeight: 1.7,
            }}>
              {analysis.summary}
            </Typography>
          </Box>
        )}

        {analysis.root_cause && (
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{
              fontFamily: '"DM Sans", sans-serif', fontSize: '0.62rem', fontWeight: 600,
              textTransform: 'uppercase', letterSpacing: '0.1em', color: colors.textMed, mb: 0.5,
              display: 'flex', alignItems: 'center', gap: 0.5,
            }}>
              <SearchIcon sx={{ fontSize: '0.85rem' }} /> Cause probable
            </Typography>
            <Typography sx={{
              fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: colors.text,
              textAlign: 'justify', lineHeight: 1.7,
            }}>
              {analysis.root_cause}
            </Typography>
          </Box>
        )}

        {analysis.urgency_level != null && (
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{
              fontFamily: '"DM Sans", sans-serif', fontSize: '0.62rem', fontWeight: 600,
              textTransform: 'uppercase', letterSpacing: '0.1em', color: colors.textMed, mb: 0.5,
              display: 'flex', alignItems: 'center', gap: 0.5,
            }}>
              <WarningAmberIcon sx={{ fontSize: '0.85rem', color: CRISIS_COLORS.or }} /> Niveau d'urgence: {analysis.urgency_level}/10
            </Typography>
            <Typography sx={{
              fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: colors.text,
              textAlign: 'justify', lineHeight: 1.7,
            }}>
              {analysis.urgency_reason}
            </Typography>
          </Box>
        )}

        {analysis.risks?.length > 0 && (
          <Box>
            <Typography sx={{
              fontFamily: '"DM Sans", sans-serif', fontSize: '0.62rem', fontWeight: 600,
              textTransform: 'uppercase', letterSpacing: '0.1em', color: colors.textMed, mb: 0.5,
              display: 'flex', alignItems: 'center', gap: 0.5,
            }}>
              <ErrorIcon sx={{ fontSize: '0.85rem', color: CRISIS_COLORS.bordeaux }} /> Risques potentiels
            </Typography>
            <Box sx={{ pl: 1 }}>
              {analysis.risks.map((risk, idx) => (
                <Typography key={idx} sx={{
                  fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: colors.text,
                  mb: 0.5, pl: 1, borderLeft: `2px solid ${CRISIS_COLORS.bordeauxMed}`,
                }}>
                  {risk}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: CRISIS_COLORS.pageBg,
      mx: { xs: -2, sm: -3, md: -7.5 },
      px: { xs: 2, sm: 3, md: 7.5 },
      pb: 6,
    }}>
      {/* ===== HEADER ===== */}
      <ScrollReveal>
        <Box sx={{
          pt: 5, pb: 4, mb: 2,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        }}>
          <Box>
            <Typography sx={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontWeight: 400,
              fontSize: { xs: '3.2rem', sm: '4.8rem' },
              color: colors.charcoal,
              lineHeight: 1.1,
              mb: 1,
            }}>
              Salle de{' '}
              <Box component="em" sx={{ color: CRISIS_COLORS.bordeaux, fontStyle: 'italic' }}>
                Crise
              </Box>
            </Typography>
            <Typography sx={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '1rem', color: colors.textMed, fontWeight: 400,
            }}>
              {brandName} — Suivi et gestion de crise en temps reel.
            </Typography>
          </Box>
          {!activeCrisis && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setDeclareDialogOpen(true)}
              sx={{
                borderColor: CRISIS_COLORS.bordeauxMed,
                color: CRISIS_COLORS.bordeaux,
                borderRadius: '100px',
                textTransform: 'none',
                fontFamily: '"DM Sans", sans-serif',
                fontWeight: 600,
                flexShrink: 0,
                '&:hover': { borderColor: CRISIS_COLORS.bordeaux, bgcolor: CRISIS_COLORS.bordeauxLight },
              }}
            >
              Declarer une crise
            </Button>
          )}
        </Box>
      </ScrollReveal>

      {/* ===== SEVERITY BANNER ===== */}
      <AnimatePresence>
        {activeCrisis && (
          <ScrollReveal delay={0.05}>
            <Box sx={{
              height: 48, display: 'flex', alignItems: 'center', gap: 2,
              px: 3, borderRadius: '12px',
              bgcolor: CRISIS_COLORS.bordeauxLight,
              border: `1px solid ${CRISIS_COLORS.bordeauxMed}`,
              mb: 3,
            }}>
              <motion.div
                animate={{ opacity: [1, 0.4, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              >
                <Box sx={{
                  width: 8, height: 8, borderRadius: '50%',
                  bgcolor: SEVERITY_COLORS[crisisData.crisis.severity] || CRISIS_COLORS.bordeaux,
                }} />
              </motion.div>
              <Typography sx={{
                fontSize: '0.82rem', fontWeight: 600,
                color: SEVERITY_COLORS[crisisData.crisis.severity],
                fontFamily: '"DM Sans", sans-serif',
              }}>
                {SEVERITY_LABELS[crisisData.crisis.severity] || 'Alerte'}
              </Typography>
              <Typography sx={{
                fontSize: '0.75rem', color: colors.textMed,
                fontFamily: '"DM Sans", sans-serif',
              }}>
                depuis {crisisData.crisis.duration_hours < 1
                  ? `${Math.round((crisisData.crisis.duration_hours || 0) * 60)}min`
                  : `${(crisisData.crisis.duration_hours || 0).toFixed(0)}h`}
              </Typography>
              <Typography sx={{
                fontSize: '0.75rem', color: colors.textLight,
                fontFamily: '"DM Sans", sans-serif', ml: 'auto',
              }}>
                {(crisisData.crisis.trigger_type || '').replace(/_/g, ' ')}
              </Typography>
            </Box>
          </ScrollReveal>
        )}
      </AnimatePresence>

      {/* ===== IMPACT SCORE ===== */}
      {activeCrisis && crisisData?.crisis?.potential_damage_score != null && (
        <ScrollReveal delay={0.1}>
          <GlassCard hover={false} glow={false} sx={{ textAlign: 'center', mb: 3 }}>
            <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
              <Typography sx={{
                fontFamily: '"DM Sans", sans-serif', fontSize: '0.62rem',
                fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.1em', color: colors.textMed, mb: 1,
              }}>
                Score d'impact
              </Typography>
              <Typography sx={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontSize: { xs: '3rem', sm: '4rem' }, fontWeight: 500,
                color: CRISIS_COLORS.bordeaux, lineHeight: 1,
                fontVariantNumeric: 'tabular-nums',
              }}>
                {Math.round(crisisData.crisis.potential_damage_score)}
                <Typography component="span" sx={{
                  fontFamily: '"Playfair Display", Georgia, serif',
                  fontSize: { xs: '1rem', sm: '1.2rem' },
                  color: colors.textLight, ml: 0.5,
                }}>
                  /100
                </Typography>
              </Typography>
              <Box sx={{
                width: 200, height: 4, borderRadius: 2,
                bgcolor: 'rgba(139,26,26,0.1)', mx: 'auto', mt: 1.5,
              }}>
                <Box sx={{
                  width: `${Math.min(100, crisisData.crisis.potential_damage_score || 0)}%`,
                  height: '100%', borderRadius: 2,
                  bgcolor: CRISIS_COLORS.bordeaux,
                  transition: 'width 0.5s ease',
                }} />
              </Box>
              <Typography sx={{
                fontSize: '0.72rem', color: colors.textMed,
                fontFamily: '"DM Sans", sans-serif', mt: 1,
              }}>
                {SEVERITY_LABELS[crisisData.crisis.severity] || 'Alerte'} — detectee le {crisisData.crisis.detected_at ? new Date(crisisData.crisis.detected_at).toLocaleString('fr-FR') : 'N/A'}
              </Typography>
            </Box>
          </GlassCard>
        </ScrollReveal>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ===== LOADING STATE ===== */}
      {loading && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rounded" height={400} sx={{ borderRadius: '20px' }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rounded" height={400} sx={{ borderRadius: '20px' }} />
          </Grid>
        </Grid>
      )}

      {/* ===== ACTIVE CRISIS VIEW ===== */}
      {!loading && activeCrisis && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            {/* Crisis Info Card */}
            <ScrollReveal delay={0.1}>
              <GlassCard sx={{ borderLeft: `3px solid ${CRISIS_COLORS.bordeaux}`, mb: 3 }}>
                <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Typography sx={{
                      fontFamily: '"Playfair Display", Georgia, serif',
                      fontSize: '1.15rem', fontWeight: 500,
                      color: CRISIS_COLORS.bordeaux,
                    }}>
                      Crise active
                    </Typography>
                    <Chip
                      label={SEVERITY_LABELS[crisisData.crisis.severity]}
                      size="small"
                      sx={{
                        bgcolor: SEVERITY_COLORS[crisisData.crisis.severity],
                        color: 'white', fontWeight: 600,
                        fontSize: '0.68rem', fontFamily: '"DM Sans", sans-serif',
                      }}
                    />
                    <Box sx={{ flex: 1 }} />
                    <Button
                      variant="outlined" size="small"
                      onClick={() => setResolveDialogOpen(true)}
                      sx={{
                        borderRadius: '100px', fontFamily: '"DM Sans", sans-serif',
                        fontSize: '0.75rem', textTransform: 'none',
                        color: colors.success, borderColor: colors.success,
                        '&:hover': { bgcolor: colors.successBg },
                      }}
                    >
                      Marquer resolue
                    </Button>
                  </Box>
                  <Box sx={{ p: 2, bgcolor: CRISIS_COLORS.bordeauxLight, borderRadius: '12px' }}>
                    <Typography sx={{
                      fontFamily: '"DM Sans", sans-serif',
                      fontSize: '0.85rem', fontWeight: 600,
                      color: colors.charcoal, mb: 0.5,
                    }}>
                      {(crisisData.crisis.trigger_type || 'Spike negatif').replace(/_/g, ' ')}
                    </Typography>
                    <Typography sx={{
                      fontFamily: '"DM Sans", sans-serif',
                      fontSize: '0.72rem', color: colors.textLight,
                    }}>
                      Detecte: {crisisData.crisis.detected_at ? new Date(crisisData.crisis.detected_at).toLocaleString('fr-FR') : 'N/A'}
                    </Typography>
                    <Typography sx={{
                      fontFamily: '"DM Sans", sans-serif',
                      fontSize: '0.82rem', color: colors.text,
                      mt: 1, textAlign: 'justify', lineHeight: 1.7,
                    }}>
                      {crisisData.crisis.trigger_description || 'Augmentation anormale des mentions negatives detectee.'}
                    </Typography>
                  </Box>
                </Box>
              </GlassCard>
            </ScrollReveal>

            {/* AI Analysis — CollapsibleSection */}
            <ScrollReveal delay={0.15}>
              <CollapsibleSection
                title="Analyse IA"
                icon={<SmartToyIcon sx={{ fontSize: 20 }} />}
                borderColor={CRISIS_COLORS.bordeaux}
                summary={crisisData.crisis.ai_analysis
                  ? (typeof crisisData.crisis.ai_analysis === 'string'
                    ? crisisData.crisis.ai_analysis.substring(0, 120) + '...'
                    : (crisisData.crisis.ai_analysis.summary || '').substring(0, 120) + '...')
                  : 'Cliquez sur Relancer pour obtenir une analyse IA'}
                info="Analyse experte de la situation par l'IA. Resume, cause probable, niveau d'urgence et risques potentiels."
                defaultOpen={!!crisisData.crisis.ai_analysis}
              >
                {renderAIAnalysis()}
                <Box sx={{ mt: 2, textAlign: 'right' }}>
                  <Button
                    size="small"
                    onClick={handleAnalyze}
                    disabled={analyzing}
                    startIcon={analyzing ? <CircularProgress size={14} /> : <PlayArrowIcon sx={{ fontSize: '0.9rem' }} />}
                    sx={{
                      borderRadius: '100px', fontSize: '0.72rem',
                      fontFamily: '"DM Sans", sans-serif',
                      color: CRISIS_COLORS.bordeaux, textTransform: 'none',
                    }}
                  >
                    {analyzing ? 'Analyse...' : 'Relancer l\'analyse'}
                  </Button>
                </Box>
              </CollapsibleSection>
            </ScrollReveal>

            {/* Action Plan — CollapsibleSection */}
            <ScrollReveal delay={0.2}>
              <CollapsibleSection
                title="Plan d'action SCCT"
                icon={<AssignmentTurnedInIcon sx={{ fontSize: 20 }} />}
                borderColor={CRISIS_COLORS.or}
                summary={actionPlan.length > 0
                  ? `${actionPlan.filter(a => a.completed).length}/${actionPlan.length} actions completees`
                  : 'Lancez l\'analyse IA pour obtenir un plan d\'action'}
                info="Plan d'action structure selon la methode SCCT (Situational Crisis Communication Theory)."
                defaultOpen
              >
                {actionPlan.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {actionPlan.map((action, idx) => (
                      <motion.div
                        key={action.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: idx * 0.05 }}
                      >
                        <Box sx={{
                          bgcolor: action.completed ? 'rgba(34, 197, 94, 0.06)' :
                                   action.priority === 'high' ? CRISIS_COLORS.bordeauxLight : 'rgba(255,255,255,0.5)',
                          borderRadius: '12px',
                          border: '1px solid',
                          borderColor: action.completed ? colors.success :
                                       action.priority === 'high' ? CRISIS_COLORS.bordeauxMed : colors.borderLight,
                          p: 1.5,
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={action.completed}
                                  onChange={() => handleToggleAction(action.id)}
                                  sx={{ color: CRISIS_COLORS.bordeaux, '&.Mui-checked': { color: colors.success } }}
                                />
                              }
                              label={
                                <Typography sx={{
                                  fontFamily: '"DM Sans", sans-serif',
                                  fontSize: '0.82rem',
                                  textDecoration: action.completed ? 'line-through' : 'none',
                                  color: action.completed ? colors.success : colors.text,
                                  fontWeight: action.priority === 'high' ? 600 : 400,
                                }}>
                                  {idx + 1}. {action.text}
                                </Typography>
                              }
                              sx={{ flex: 1, m: 0 }}
                            />
                          </Box>
                          {(action.priority || action.timeline || action.category) && (
                            <Box sx={{ display: 'flex', gap: 1, ml: 4, mt: 0.5, flexWrap: 'wrap' }}>
                              {action.priority && (
                                <Chip
                                  icon={action.priority === 'high' ? <BoltIcon sx={{ fontSize: '0.75rem' }} /> :
                                        action.priority === 'medium' ? <PushPinIcon sx={{ fontSize: '0.75rem' }} /> : <AssignmentIcon sx={{ fontSize: '0.75rem' }} />}
                                  label={action.priority === 'high' ? 'Urgent' : action.priority === 'medium' ? 'Important' : 'Normal'}
                                  size="small"
                                  sx={{
                                    fontSize: '0.68rem', fontFamily: '"DM Sans", sans-serif',
                                    bgcolor: action.priority === 'high' ? CRISIS_COLORS.bordeauxMed :
                                             action.priority === 'medium' ? CRISIS_COLORS.orLight : colors.gray200,
                                    color: action.priority === 'high' ? CRISIS_COLORS.bordeaux :
                                           action.priority === 'medium' ? CRISIS_COLORS.or : '#475569',
                                    '& .MuiChip-icon': { color: 'inherit' },
                                  }}
                                />
                              )}
                              {action.timeline && (
                                <Chip
                                  icon={<AccessTimeIcon sx={{ fontSize: '0.75rem' }} />}
                                  label={action.timeline}
                                  size="small"
                                  sx={{ fontSize: '0.68rem', fontFamily: '"DM Sans", sans-serif', bgcolor: '#E0E7FF', color: '#3730A3', '& .MuiChip-icon': { color: 'inherit' } }}
                                />
                              )}
                              {action.category && (
                                <Chip label={action.category} size="small" variant="outlined"
                                  sx={{ fontSize: '0.68rem', fontFamily: '"DM Sans", sans-serif' }} />
                              )}
                            </Box>
                          )}
                        </Box>
                      </motion.div>
                    ))}
                  </Box>
                ) : (
                  <Typography sx={{
                    fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem',
                    color: colors.textLight, textAlign: 'center', py: 2,
                  }}>
                    Lancez l'analyse IA pour obtenir un plan d'action personnalise.
                  </Typography>
                )}
              </CollapsibleSection>
            </ScrollReveal>

            {/* Propagation Timeline — CollapsibleSection */}
            {timeline.length > 0 && (
              <ScrollReveal delay={0.25}>
                <CollapsibleSection
                  title="Propagation"
                  borderColor={CRISIS_COLORS.bordeaux}
                  summary={`${timeline.length} evenements detectes`}
                  info="Chronologie de propagation de la crise a travers les differentes plateformes et auteurs."
                  defaultOpen={false}
                >
                  <Box sx={{ pl: 2, borderLeft: `2px solid ${CRISIS_COLORS.bordeauxMed}` }}>
                    {timeline.map((item, idx) => (
                      <motion.div
                        key={item.id || idx}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: idx * 0.05 }}
                      >
                        <Box sx={{ mb: 2, position: 'relative' }}>
                          <Box sx={{
                            position: 'absolute', left: -11, top: 4,
                            width: 8, height: 8, borderRadius: '50%',
                            bgcolor: item.sentiment === 'negative' ? CRISIS_COLORS.bordeaux : CRISIS_COLORS.or,
                            border: `2px solid ${CRISIS_COLORS.pageBg}`,
                          }} />
                          <Box sx={{ pl: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                              <Chip label={item.platform} size="small" sx={{
                                height: 18, fontSize: '0.62rem',
                                fontFamily: '"DM Sans", sans-serif',
                                bgcolor: 'rgba(139,26,26,0.06)', color: colors.textMed,
                              }} />
                              <Typography sx={{
                                fontSize: '0.72rem', color: colors.textLight,
                                fontFamily: '"DM Sans", sans-serif',
                              }}>
                                {item.published_at ? new Date(item.published_at).toLocaleString('fr-FR', {
                                  hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short',
                                }) : ''}
                              </Typography>
                              {item.author_followers > 10000 && (
                                <Chip label={`${(item.author_followers / 1000).toFixed(0)}K`} size="small" sx={{
                                  height: 16, fontSize: '0.58rem',
                                  bgcolor: CRISIS_COLORS.bordeauxMed, color: CRISIS_COLORS.bordeaux,
                                }} />
                              )}
                            </Box>
                            <Typography sx={{
                              fontSize: '0.75rem', fontWeight: 500, color: colors.text,
                              fontFamily: '"DM Sans", sans-serif',
                            }}>
                              @{item.author_username}
                            </Typography>
                            <Typography sx={{
                              fontSize: '0.72rem', color: colors.textMed,
                              fontFamily: '"DM Sans", sans-serif', mt: 0.25,
                            }}>
                              {item.text}
                            </Typography>
                          </Box>
                        </Box>
                      </motion.div>
                    ))}
                  </Box>
                </CollapsibleSection>
              </ScrollReveal>
            )}

            {/* Situation Report — CollapsibleSection */}
            {crisisData?.crisis && (
              <ScrollReveal delay={0.3}>
                <CollapsibleSection
                  title="Rapport de situation"
                  borderColor={CRISIS_COLORS.or}
                  summary={report
                    ? `Severite: ${report.severity_label} — ${(report.duration_hours || 0).toFixed(1)}h`
                    : 'Generer le rapport pour obtenir un bilan complet'}
                  info="Bilan synthetique de la crise: severite, duree, portee, plateformes affectees et analyse narrative."
                  defaultOpen={false}
                >
                  {report ? (
                    <Box>
                      <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
                        {[
                          { label: 'Severite', value: report.severity_label },
                          { label: 'Duree', value: `${(report.duration_hours || 0).toFixed(1)}h` },
                          { label: 'Portee', value: (report.estimated_reach || 0).toLocaleString() },
                          { label: 'Plateformes', value: (report.affected_platforms || []).join(', ') || '-' },
                        ].map(({ label, value }) => (
                          <Box key={label}>
                            <Typography sx={{
                              fontSize: '0.62rem', color: colors.textLight,
                              fontFamily: '"DM Sans", sans-serif',
                              textTransform: 'uppercase', letterSpacing: '0.1em',
                            }}>
                              {label}
                            </Typography>
                            <Typography sx={{
                              fontSize: '0.85rem', fontWeight: 600, color: colors.charcoal,
                              fontFamily: '"DM Sans", sans-serif',
                            }}>
                              {value}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      {report.narrative && (
                        <Typography sx={{
                          fontSize: '0.82rem', color: colors.text,
                          fontFamily: '"DM Sans", sans-serif',
                          lineHeight: 1.7, textAlign: 'justify',
                        }}>
                          {report.narrative}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center' }}>
                      <Button
                        variant="outlined" size="small"
                        onClick={() => fetchReport(crisisData.crisis.id)}
                        disabled={reportLoading}
                        sx={{
                          borderRadius: '100px', fontSize: '0.72rem',
                          fontFamily: '"DM Sans", sans-serif',
                          color: CRISIS_COLORS.bordeaux, borderColor: CRISIS_COLORS.bordeauxMed,
                          textTransform: 'none',
                          '&:hover': { borderColor: CRISIS_COLORS.bordeaux, bgcolor: CRISIS_COLORS.bordeauxLight },
                        }}
                      >
                        {reportLoading ? 'Generation...' : 'Generer le rapport'}
                      </Button>
                    </Box>
                  )}
                </CollapsibleSection>
              </ScrollReveal>
            )}
          </Grid>

          {/* RIGHT COLUMN: Simulation + Historique */}
          <Grid item xs={12} md={4}>
            {/* Simulation */}
            <ScrollReveal delay={0.15}>
              <GlassCard sx={{ borderLeft: `3px solid ${CRISIS_COLORS.or}`, mb: 3 }}>
                <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
                  <Typography sx={{
                    fontFamily: '"DM Sans", sans-serif', fontSize: '0.62rem',
                    fontWeight: 600, textTransform: 'uppercase',
                    letterSpacing: '0.1em', color: colors.textMed, mb: 1,
                    display: 'flex', alignItems: 'center', gap: 1,
                  }}>
                    <ScienceIcon sx={{ fontSize: '1rem', color: CRISIS_COLORS.or }} /> Simulation
                  </Typography>
                  <Typography sx={{
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: '0.75rem', color: colors.textLight, mb: 2,
                  }}>
                    Anticipez l'evolution de la crise via recherche Perplexity:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {[
                      { key: 'influencer', label: "Et si un influenceur majeur s'en mele?" },
                      { key: 'media', label: "Et si un media national reprend l'info?" },
                      { key: 'duration', label: "Et si la crise dure 48h de plus?" },
                    ].map(({ key, label }) => (
                      <Button
                        key={key} variant="outlined" size="small" fullWidth
                        onClick={() => handleSimulation(key)}
                        disabled={simulating !== null}
                        startIcon={simulating === key ? <CircularProgress size={14} /> : null}
                        sx={{
                          borderRadius: '100px', fontSize: '0.72rem',
                          fontFamily: '"DM Sans", sans-serif',
                          textTransform: 'none', textAlign: 'left', justifyContent: 'flex-start',
                          color: CRISIS_COLORS.bordeaux, borderColor: CRISIS_COLORS.bordeauxMed,
                          '&:hover': { borderColor: CRISIS_COLORS.bordeaux, bgcolor: CRISIS_COLORS.bordeauxLight },
                        }}
                      >
                        {simulating === key ? 'Simulation...' : label}
                      </Button>
                    ))}
                  </Box>

                  <AnimatePresence>
                    {simulationResults && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        style={{ overflow: 'hidden' }}
                      >
                        <Box sx={{
                          mt: 2, p: 2,
                          bgcolor: CRISIS_COLORS.bordeauxLight,
                          borderRadius: '12px',
                          border: `1px solid ${CRISIS_COLORS.bordeauxMed}`,
                        }}>
                          <Typography sx={{
                            fontFamily: '"DM Sans", sans-serif',
                            fontSize: '0.62rem', fontWeight: 600,
                            textTransform: 'uppercase', letterSpacing: '0.1em',
                            color: colors.charcoal, mb: 0.5,
                            display: 'flex', alignItems: 'center', gap: 0.5,
                          }}>
                            <AssessmentIcon sx={{ fontSize: '0.85rem', color: CRISIS_COLORS.bordeaux }} /> Resultat
                          </Typography>
                          <Typography sx={{
                            fontFamily: '"DM Sans", sans-serif',
                            fontSize: '0.75rem', color: colors.text, mb: 1,
                          }}>
                            {simulationResults.total} resultats analyses ({simulationResults.negative} negatifs)
                          </Typography>
                          <Typography sx={{
                            fontFamily: '"DM Sans", sans-serif',
                            fontSize: '0.72rem', color: colors.textMed,
                            textAlign: 'justify', lineHeight: 1.6,
                          }}>
                            {simulationResults.analysis}
                          </Typography>
                          {simulationResults.sample?.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography sx={{
                                fontFamily: '"DM Sans", sans-serif',
                                fontSize: '0.62rem', fontWeight: 600, color: colors.textMed,
                                textTransform: 'uppercase', letterSpacing: '0.1em',
                              }}>Exemples:</Typography>
                              {simulationResults.sample.map((item, idx) => (
                                <Typography key={idx} sx={{
                                  fontFamily: '"DM Sans", sans-serif',
                                  fontSize: '0.68rem', color: colors.textLight, ml: 1, mt: 0.5,
                                }}>
                                  - {item.text?.substring(0, 80)}...
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Box>
              </GlassCard>
            </ScrollReveal>

            {/* Crisis History */}
            <ScrollReveal delay={0.2}>
              <GlassCard sx={{ mb: 3 }}>
                <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
                  <Typography sx={{
                    fontFamily: '"DM Sans", sans-serif', fontSize: '0.62rem',
                    fontWeight: 600, textTransform: 'uppercase',
                    letterSpacing: '0.1em', color: colors.textMed, mb: 2,
                    display: 'flex', alignItems: 'center', gap: 1,
                  }}>
                    <HistoryIcon sx={{ fontSize: '1rem' }} /> Historique
                  </Typography>
                  <CrisisHistoryList crisisHistory={crisisHistory} />
                </Box>
              </GlassCard>
            </ScrollReveal>
          </Grid>
        </Grid>
      )}

      {/* ===== NO ACTIVE CRISIS VIEW ===== */}
      {!loading && !activeCrisis && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <ScrollReveal delay={0.1}>
              <GlassCard sx={{ textAlign: 'center' }}>
                <Box sx={{ p: { xs: 3, md: 4 } }}>
                  <CheckCircleIcon sx={{ fontSize: 40, color: colors.success, mb: 2, opacity: 0.6 }} />
                  <Typography sx={{
                    fontFamily: '"Playfair Display", Georgia, serif',
                    fontSize: '1.3rem', fontWeight: 500,
                    color: colors.success, mb: 1,
                  }}>
                    Situation stable
                  </Typography>
                  <Typography sx={{
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: '0.85rem', color: colors.textMed,
                    textAlign: 'justify',
                    maxWidth: 500, mx: 'auto',
                  }}>
                    Aucune crise n'est actuellement detectee pour cette marque.
                    Le systeme surveille en continu les mentions et vous alertera automatiquement
                    en cas de detection d'une situation anormale.
                  </Typography>
                </Box>
              </GlassCard>
            </ScrollReveal>
          </Grid>
          <Grid item xs={12} md={4}>
            <ScrollReveal delay={0.15}>
              <GlassCard sx={{ mb: 3 }}>
                <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
                  <Typography sx={{
                    fontFamily: '"DM Sans", sans-serif', fontSize: '0.62rem',
                    fontWeight: 600, textTransform: 'uppercase',
                    letterSpacing: '0.1em', color: colors.textMed, mb: 2,
                    display: 'flex', alignItems: 'center', gap: 1,
                  }}>
                    <HistoryIcon sx={{ fontSize: '1rem' }} /> Historique
                  </Typography>
                  <CrisisHistoryList crisisHistory={crisisHistory} />
                </Box>
              </GlassCard>
            </ScrollReveal>
          </Grid>
        </Grid>
      )}

      {/* ===== DIALOGS ===== */}

      {/* Resolve Dialog */}
      <Dialog open={resolveDialogOpen} onClose={() => setResolveDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '20px', bgcolor: colors.ivory } }}
      >
        <DialogTitle sx={{
          fontFamily: '"Playfair Display", Georgia, serif',
          fontSize: '1.2rem', fontWeight: 500, color: colors.charcoal,
        }}>
          Resoudre la crise
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: colors.textMed, mb: 2 }}>
            Ajoutez des notes sur la resolution de cette crise:
          </Typography>
          <TextField
            fullWidth multiline rows={4}
            placeholder="Notes de resolution..."
            value={resolveNotes}
            onChange={(e) => setResolveNotes(e.target.value)}
            sx={{ '& .MuiInputBase-root': { fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem', borderRadius: '12px' } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResolveDialogOpen(false)} sx={{
            borderRadius: '100px', fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', textTransform: 'none',
          }}>Annuler</Button>
          <Button variant="contained" onClick={handleResolveCrisis} sx={{
            borderRadius: '100px', fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', textTransform: 'none',
            bgcolor: colors.success, '&:hover': { bgcolor: colors.success, opacity: 0.9 },
          }}>
            Confirmer la resolution
          </Button>
        </DialogActions>
      </Dialog>

      {/* Declare Crisis Dialog */}
      <Dialog open={declareDialogOpen} onClose={() => setDeclareDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '20px', bgcolor: colors.ivory } }}
      >
        <DialogTitle sx={{
          fontFamily: '"Playfair Display", Georgia, serif',
          fontSize: '1.2rem', fontWeight: 500, color: colors.charcoal,
        }}>
          Declarer une crise
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, borderRadius: '12px', fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem' }}>
            Cette fonctionnalite permet de declarer manuellement une crise si vous avez identifie
            une situation qui n'a pas ete detectee automatiquement.
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Box>
              <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', fontWeight: 600, color: colors.charcoal, mb: 1 }}>
                Niveau de severite
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {[1, 2, 3, 4].map((level) => (
                  <Chip
                    key={level}
                    label={SEVERITY_LABELS[level]}
                    onClick={() => setDeclareForm(prev => ({ ...prev, severity: level }))}
                    sx={{
                      fontFamily: '"DM Sans", sans-serif', fontSize: '0.72rem',
                      bgcolor: declareForm.severity === level ? SEVERITY_COLORS[level] : 'transparent',
                      color: declareForm.severity === level ? 'white' : colors.text,
                      border: `1px solid ${SEVERITY_COLORS[level]}`, cursor: 'pointer',
                    }}
                  />
                ))}
              </Box>
            </Box>
            <TextField
              fullWidth multiline rows={3}
              label="Description de la crise"
              placeholder="Decrivez la situation qui justifie la declaration de crise..."
              value={declareForm.description}
              onChange={(e) => setDeclareForm(prev => ({ ...prev, description: e.target.value }))}
              sx={{ '& .MuiInputBase-root': { fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem', borderRadius: '12px' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeclareDialogOpen(false)} sx={{
            borderRadius: '100px', fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', textTransform: 'none',
          }}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleDeclareCrisis}
            disabled={!declareForm.description.trim()}
            sx={{
              borderRadius: '100px', fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', textTransform: 'none',
              bgcolor: CRISIS_COLORS.bordeaux, '&:hover': { bgcolor: CRISIS_COLORS.bordeaux, opacity: 0.9 },
            }}
          >
            Declarer la crise
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CrisisPage;
